import { useState } from "react";
import { useApolloClient } from "@apollo/client";
import { useParams } from "react-router-dom";
import { IParams } from "@models/IParams";
import { Campus, PageInfo } from "@models/ISchema";
import { TableFiltersReducerType } from "../context/types";
import { shortGroupsSearch, groupsSearch as subgroups } from "../graphql/groups.query";
import { groupAdapter } from "../adapters/group";
import { AdaptedGroup } from "../models";

interface IPagination {
  page: number;
  size: number;
}

interface ShortGroup {
  id: string;
  capacity: number;
  campus: Campus;
}

export const useGetSubgroups = (): [
  {
    subgroups: AdaptedGroup[];
    shortSubgroups: ShortGroup[];
    isLoading: boolean;
    subgroupsPageInfo: PageInfo;
  },
  {
    getShortGroupsData: (
      group: AdaptedGroup,
      pagination: IPagination,
      orderBy: TableFiltersReducerType["orderBy"],
      searchBy: TableFiltersReducerType["searchBy"],
    ) => void;
    getSubgroups: (
      group: AdaptedGroup,
      pagination: IPagination,
      orderBy: TableFiltersReducerType["orderBy"],
      searchBy: TableFiltersReducerType["searchBy"],
    ) => void;
  },
] => {
  const { scenario, origin }: IParams = useParams();
  const client = useApolloClient();
  const [response, setResponse] = useState<AdaptedGroup[]>(null);
  const [pageInfo, setPageInfo] = useState<PageInfo>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [shortGroupsLoading, setShortGroupsLoading] = useState<boolean>(false);
  const [shortGroups, setShortGroups] = useState<ShortGroup[]>([]);

  const variables = (
    group: AdaptedGroup,
    pagination: IPagination,
    orderBy: TableFiltersReducerType["orderBy"],
    searchBy: TableFiltersReducerType["searchBy"],
  ) => {
    const fields = {
      isGrouped: { is: false },
      linkId: group?.link ? { eq: group?.link?.id } : undefined,
    };

    return {
      scenarioId: scenario,
      originId: origin,
      filter: {
        fields,
        pagination,
        orderBy:
          !orderBy || Object.keys(orderBy).length === 0 || orderBy?.field == null
            ? undefined
            : (() => {
                const orderByWithoutHeader = { ...orderBy };
                delete orderByWithoutHeader.header;
                return orderByWithoutHeader;
              })(),
        searchBy:
          !searchBy || Object.keys(searchBy).length === 0 || searchBy?.text == null
            ? undefined
            : searchBy,
      },
    };
  };

  const getShortGroupsData = async (
    group: AdaptedGroup,
    pagination: IPagination,
    orderBy: TableFiltersReducerType["orderBy"],
    searchBy: TableFiltersReducerType["searchBy"],
  ) => {
    setShortGroupsLoading(true);
    setShortGroups([]);

    const { data, errors } = await client.query({
      query: shortGroupsSearch,
      variables: variables(group, pagination, orderBy, searchBy),
      fetchPolicy: "no-cache",
    });

    if (errors) {
      console.error("[Subgroups error]", errors);
      setShortGroupsLoading(false);
      return null;
    }

    setShortGroupsLoading(false);
    setShortGroups(data?.cube?.groupsManagerSearch?.items ?? []);
  };

  const getSubgroups = async (
    group: AdaptedGroup,
    pagination: IPagination,
    orderBy: TableFiltersReducerType["orderBy"],
    searchBy: TableFiltersReducerType["searchBy"],
  ) => {
    setLoading(true);
    try {
      const { data, errors } = await client.query({
        query: subgroups,
        variables: variables(group, pagination, orderBy, searchBy),
        fetchPolicy: "no-cache",
      });

      if (errors) {
        console.error("[Subgroups error]", errors);
        setLoading(false);
        return null;
      }

      const adaptedResponse = data?.cube?.groupsManagerSearch?.items?.map(groupAdapter) ?? [];

      setResponse(adaptedResponse);
      setPageInfo(data?.cube?.groupsManagerSearch?.pageInfo);
      setLoading(false);
    } catch (error) {
      console.error("[Subgroups error]: ", error);
      setLoading(false);
    }
  };

  return [
    {
      subgroups: response,
      shortSubgroups: shortGroups,
      isLoading: loading || shortGroupsLoading,
      subgroupsPageInfo: pageInfo,
    },
    { getSubgroups, getShortGroupsData },
  ];
};
